import { Autocomplete, Box, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material'
import { FC, Fragment, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache, useGetCustomerLocalities } from 'src/api/localities'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { invalidateMainComponentCache, useBulkSendToLocality } from 'src/api/main-component'
import { invalidateMooringCache } from 'src/api/moorings'
import { useGetCagePositionsByLocality } from 'src/api/positions'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { AppTextField } from 'src/components/AppTextField'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'
import { MovementType } from 'src/enums'
import { ILocality, IMooring, IPosition } from 'src/generated-types'
import { ChangeLocationType } from 'src/generated-types/change-location-type'
import { IMainComponent } from 'src/generated-types/main-component'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { sortByName } from 'src/utils'
import { getMooringName, getPositionName, getUTCDate } from 'src/utils/function'
import { z } from 'zod'

export interface BulkSendToLocalityDialogProps extends ShowDialogProps {
  customerId: EntityId
  selectedEquipments: IMainComponent[]
  /** Optional default locality when opening (e.g. from context) */
  defaultLocalityId?: EntityId
  onSuccess?: () => void
}

/** Single schema with all fields so form type accepts both locality and position modes */
const bulkSendToLocalityFormSchema = z.object({
  selectedLocalityId: z.number(),
  positionId: z.number().optional(),
  positionName: z.string().optional(),
  effectiveDate: z
    .date()
    .or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v)))
    .optional(),
})

const BulkSendToLocalityDialog: FC<BulkSendToLocalityDialogProps> = ({
  customerId,
  selectedEquipments,
  defaultLocalityId = 0,
  pop,
  onSuccess,
}) => {
  const { mutateAsync: bulkSendToLocality } = useBulkSendToLocality(customerId as EntityId)
  const { data: localities } = useGetCustomerLocalities({ clientId: customerId as EntityId })
  const [formType, setFormType] = useState<ChangeLocationType>(ChangeLocationType.MoveToLocality)
  const [selectedMooringId, setSelectedMooringId] = useState<EntityId | undefined>()
  const [targetLocalityId, setTargetLocalityId] = useState<EntityId | undefined>(defaultLocalityId || undefined)
  const { data: positionsData } = useGetCagePositionsByLocality(
    customerId,
    (targetLocalityId ?? defaultLocalityId ?? 0) as EntityId,
  )

  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)

  const equipmentTypeIds = useMemo(
    () => Array.from(new Set(selectedEquipments.map(e => e.type?.id).filter(Boolean) as number[])),
    [selectedEquipments],
  )

  const { data: sendToLocalitySchemas, isLoading: isLoadingSendToLocalitySchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds, movementTypes: [MovementType.SendToLocality] },
  )

  const { data: changePositionSchemas, isLoading: isLoadingChangePositionSchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds, movementTypes: [MovementType.ChangePosition] },
  )

  const { t } = useTranslation(['common', 'main-component', 'service-station'])
  const form = useZodForm(bulkSendToLocalityFormSchema, {
    defaultValues: {
      selectedLocalityId: defaultLocalityId ?? selectedEquipments[0]?.selectedLocalityId ?? 0,
    },
  })

  /** Include all selected equipment; use 0 for localityId when not yet in a locality (e.g. Newly Registered) */
  const items = useMemo(
    () =>
      selectedEquipments
        .filter(e => e.id != null)
        .map(e => ({
          id: e.id as number,
          localityId: (e.selectedLocalityId ?? 0) as number,
        })),
    [selectedEquipments],
  )

  const mooringList = useMemo(
    () =>
      (positionsData ?? []).reduce((moorings, currentValue) => {
        if (!moorings.some(mooring => mooring.id === currentValue.mooringId)) {
          moorings.push({
            id: currentValue.mooringId,
            name: currentValue.mooringName,
          } as IMooring)
        }
        return moorings
      }, [] as IMooring[]) as IMooring[],
    [positionsData],
  )

  const invalidateOnSuccess = useCallback(
    (formValues: { selectedLocalityId?: number; positionId?: number }) => {
      invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
      const selLocId = formValues.selectedLocalityId ?? targetLocalityId ?? defaultLocalityId
      if (selLocId) {
        invalidateLocalityCache.getCageStatus(customerId, selLocId)
        invalidateMainComponentCache.getMainComponents(
          customerId as EntityId,
          selLocId as EntityId,
          selectedMooringId as EntityId,
          formValues.positionId,
        )
        invalidateMainComponentCache.getOutgoingMainComponents(customerId, selLocId)
        if (formValues.positionId) {
          invalidateCertificateCache.getCertificatesByPosition(customerId, formValues.positionId)
        }
        invalidateMainComponentCache.invalidateEquipment(customerId)
        selectedEquipments.forEach(e => {
          invalidateMainComponentCache.getMainComponentById(customerId as EntityId, e.id as EntityId)
        })
        invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, selLocId)
        invalidateDeviation.getDeviations(customerId as EntityId)
        invalidateMooringCache.getMooringsForMap(customerId, selLocId)
        invalidateDeviation.invalidateAllDeviations(customerId as EntityId, selLocId)
        invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, selLocId as EntityId)
        invalidateCustomer.getCustomersActivityCounts(customerId)
        invalidateLocalityCache.getMaintenances(customerId, selLocId)
        invalidateLocalityCache.getMaintenanceTypesCount(customerId, selLocId)
        invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
        invalidateDocument.getDocuments(customerId)
        invalidateDocument.getDocumentsForApproval(customerId, selLocId)
        invalidateLocalityCache.getCageStatus(customerId, selLocId)
        invalidateLocalityCache.getLocality(customerId, selLocId)
      }
    },
    [customerId, defaultLocalityId, selectedEquipments, targetLocalityId, selectedMooringId],
  )

  const handleSubmit = useCallback(
    async (formValues: Record<string, unknown>) => {
      if (items.length === 0) return
      const selectedLocalityId =
        (formValues.selectedLocalityId as number) ??
        (targetLocalityId as number) ??
        (selectedEquipments[0]?.selectedLocalityId as number) ??
        0
      const payload =
        formType === ChangeLocationType.MoveToPosition
          ? {
              items,
              selectedLocalityId,
              selectedPositionId: formValues.positionId as number,
              installationDate: formValues.effectiveDate as Date | string | undefined,
              isForceAddedToLocality: false,
            }
          : {
              items,
              selectedLocalityId,
              isForceAddedToLocality: false,
            }

      const schemas = formType === ChangeLocationType.MoveToPosition ? changePositionSchemas : sendToLocalitySchemas
      const isLoadingSchemas =
        formType === ChangeLocationType.MoveToPosition ? isLoadingChangePositionSchemas : isLoadingSendToLocalitySchemas

      await showChecklistsDialog({
        isBulk: true,
        schemas,
        isLoadingSchemas,
        selectedEquipments,
        maxCountValidation:
          formType === ChangeLocationType.MoveToPosition
            ? { positionId: payload.selectedPositionId, equipmentIds: items.map(item => item.id) }
            : undefined,
        onConfirm: async equipmentReportsMap => {
          const itemsWithReports = items.map(item => ({
            ...item,
            formSchemaReports: equipmentReportsMap.get(item.id) ?? [],
          }))

          await bulkSendToLocality(
            { ...payload, items: itemsWithReports },
            {
              onSuccess: () => {
                invalidateOnSuccess({
                  selectedLocalityId: payload.selectedLocalityId,
                  positionId: payload.selectedPositionId,
                })
                onSuccess?.()
                toast.success(t('successfully-sent-to-locality', { ns: 'main-component' }))
                pop()
              },
              onError: error => {
                const msg =
                  error?.response?.data === 'ExceedMaxCountForCage'
                    ? t('exceedMaxCountForCage', { ns: 'main-component' })
                    : t('updateFailed', { ns: 'common', defaultValue: 'Update failed' })
                toast.error(msg)
              },
            },
          )
        },
      })
    },
    [
      items,
      formType,
      bulkSendToLocality,
      invalidateOnSuccess,
      pop,
      onSuccess,
      t,
      targetLocalityId,
      selectedEquipments,
      showChecklistsDialog,
      sendToLocalitySchemas,
      changePositionSchemas,
      isLoadingSendToLocalitySchemas,
      isLoadingChangePositionSchemas,
      customerId,
    ],
  )

  const selectedLocalityId = form.watch('selectedLocalityId')
  const positionId = form.watch('positionId')
  const disabledAction = useMemo(() => {
    if (formType === ChangeLocationType.MoveToPosition) {
      return !selectedMooringId || !positionId || selectedLocalityId == null || selectedLocalityId === 0
    }
    return selectedLocalityId == null || selectedLocalityId === 0
  }, [formType, selectedMooringId, positionId, selectedLocalityId])

  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      <GenericDialogLayout
        title={t('sendToLocality', { ns: 'main-component' })}
        pop={pop}
        removedCloseButton={false}
        actions={[
          <FormSubmitButton
            disabled={disabledAction || items.length === 0}
            key="send"
            createText={t('sendToLocality', { ns: 'main-component' })}
          />,
        ]}
        topActions={[
          <Flex.Row key="top-action-list" width="100%" px={4}>
            <FormControl key="task-type-buttons-group">
              <RadioGroup
                value={formType}
                onChange={(_, v) => {
                  const values = Object.values(ChangeLocationType)
                  setFormType(ChangeLocationType[values[v as unknown as number] as keyof typeof ChangeLocationType])
                }}
                row
                name="task-type-buttons-group"
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  key="move-to-locality"
                  value={ChangeLocationType.MoveToLocality}
                  control={<Radio />}
                  label={t('move-to-locality', { ns: 'main-component' }) as string}
                />
                <FormControlLabel
                  key="move-to-position"
                  value={ChangeLocationType.MoveToPosition}
                  control={<Radio />}
                  label={t('move-to-position', { ns: 'main-component' }) as string}
                />
              </RadioGroup>
            </FormControl>
          </Flex.Row>,
        ]}
      >
        {formType === ChangeLocationType.MoveToPosition && (
          <Fragment>
            <Autocomplete
              value={
                (localities ?? []).find(l => l.id === (form.watch('selectedLocalityId') ?? targetLocalityId)) ?? null
              }
              onChange={(_, value) => {
                const loc = value as ILocality
                form.setValue('selectedLocalityId', loc?.id ?? 0, { shouldValidate: true })
                setTargetLocalityId(loc?.id)
                setSelectedMooringId(undefined)
                form.setValue('positionId', 0, { shouldValidate: true })
                form.setValue('positionName', '', { shouldValidate: true })
              }}
              options={(localities?.sort(sortByName) ?? []) as ILocality[]}
              autoHighlight
              getOptionLabel={o => (o as ILocality)?.name ?? ''}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {(o as ILocality).name}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectALocality', { ns: 'service-station' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => (option as ILocality)?.id === (value as ILocality)?.id}
            />
            <Autocomplete
              value={mooringList?.find(m => m.id === selectedMooringId) ?? null}
              onChange={(_, value) => {
                setSelectedMooringId((value as IMooring)?.id)
              }}
              options={(mooringList?.sort(sortByName) || []) as IMooring[]}
              autoHighlight
              getOptionLabel={o => getMooringName(o.name)}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectMooring', { ns: 'main-component' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              value={(positionsData ?? []).find(p => p.id === form.watch('positionId')) ?? null}
              onChange={(_, value) => {
                const pos = value as IPosition
                form.setValue('positionId', pos?.id ?? 0, { shouldValidate: true })
                form.setValue('positionName', pos?.name ?? '', { shouldValidate: true })
              }}
              disabled={!selectedMooringId}
              options={
                (positionsData ?? [])
                  .filter(x => x.mooringId === selectedMooringId)
                  .sort((a, b) => parseInt(a.name) - parseInt(b.name)) as IPosition[]
              }
              autoHighlight
              getOptionLabel={o => getPositionName(o) as string}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {getPositionName(o) as string}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectCage', { ns: 'main-component' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Box py={1}>
              <BasicDatePicker label={t('effectiveDate', { ns: 'main-component' })} name="effectiveDate" />
            </Box>
          </Fragment>
        )}

        {formType === ChangeLocationType.MoveToLocality && (
          <Fragment>
            <Autocomplete
              value={
                (localities ?? []).find(l => l.id === (form.watch('selectedLocalityId') ?? targetLocalityId)) ?? null
              }
              onChange={(_, value) => {
                const loc = value as ILocality
                form.setValue('selectedLocalityId', loc?.id ?? 0, { shouldValidate: true })
                setTargetLocalityId(loc?.id)
              }}
              options={(localities?.sort(sortByName) ?? []) as ILocality[]}
              autoHighlight
              getOptionLabel={o => o?.name ?? ''}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectALocality', { ns: 'service-station' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          </Fragment>
        )}
      </GenericDialogLayout>
    </AppForm>
  )
}

export default BulkSendToLocalityDialog
